import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Check } from "lucide-react";
import { InputField } from "../../components/Elements/InputField/InputField";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";
import { Eye, EyeOff } from "lucide-react";

export const ResetPassword = () => {
  const [email, setEmail] = useState({ email: "" });
  const [emailSent, setEmailSent] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [popup, setPopup] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumbers: false,
    hasSpecialChar: false,
  });

  const navigate = useNavigate();

  // Auto-hide popup after 2 seconds
  useEffect(() => {
    if (popup) {
      const timer = setTimeout(() => {
        setPopup(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [popup]);

  // Password strength checker
  useEffect(() => {
    setPasswordStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumbers: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  }, [password]);

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmail({ [name]: value });
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setConfirmPasswordError("");
  };

  const handleConfirmPassword = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== password) {
      setConfirmPasswordError("Passwords do not match");
    } else {
      setConfirmPasswordError("");
    }
  };

  const validateEmail = (emailValue) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailValue);
  };

  const handleSendEmail = (e) => {
    e.preventDefault();
    if (!email.email) {
      setPopup({
        type: "warning",
        title: "Missing Email",
        message: "Please fill the email address.",
      });
    } else if (!validateEmail(email.email)) {
      setPopup({
        type: "warning",
        title: "Email is Invalid",
        message: "Please enter a valid email address (e.g., name@example.com).",
      });
    } else {
      setEmailSent(true);
      setPopup({
        type: "success",
        title: "Email Sent",
        message: "Reset link sent to your email.",
      });
      setTimeout(() => {}, 2000);
    }
  };

  const handleResetPassword = () => {
    if (
      !passwordStrength.minLength ||
      !passwordStrength.hasUpperCase ||
      !passwordStrength.hasLowerCase ||
      !passwordStrength.hasNumbers ||
      !passwordStrength.hasSpecialChar
    ) {
      setPopup({
        type: "warning",
        title: "Weak Password",
        message: "Password must meet all the required conditions.",
      });
      return;
    }

    if (password !== confirmPassword) {
      setPopup({
        type: "warning",
        title: "Password Mismatch",
        message: "Confirm password does not match new password.",
      });
      return;
    }

    setPopup({
      type: "success",
      title: "Password Updated",
      message: "Your password has been updated successfully.",
    });
    setTimeout(() => {
      navigate("/organization/login");
    }, 2000);
  };

  const onCloseReset = () => {
    navigate(-1);
  };

  return (
    <>
      {/* Popup */}
      {popup && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black bg-opacity-30">
          <PopupCard
            type={popup.type}
            title={popup.title}
            message={popup.message}
          />
        </div>
      )}

      {/* Step 1: Email Modal */}
      {!emailSent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          <div className="py-6 px-6 shadow-lg rounded-lg sm:px-8 w-full max-w-md relative">
            <div className="flex justify-end items-center mb-4">
              <button
                onClick={onCloseReset}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#06513D] mb-1">
                CARBURN ADMIN
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Carbon Footprint & ESG Reporting
              </p>
              <h3 className="text-xl font-semibold text-[#06513D] mb-2">
                Forgot Password
              </h3>
              <p className="text-sm text-gray-600">
                Enter your registered email address. We'll send you a link to
                reset your password.
              </p>
            </div>

            <form onSubmit={handleSendEmail} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="text"
                  className="appearance-none block w-full px-3 py-2 border rounded-md placeholder-gray-400 focus:outline-none sm:text-sm border-gray-300 focus:border-[#06513D]"
                  placeholder="your@email.com"
                  value={email.email}
                  onChange={handleEmailChange}
                />
              </div>
              <button
                type="submit"
                className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-[#06513D] hover:bg-[#054a35] transition-colors"
              >
                Send Reset Link
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Step 2: Password Reset Modal */}
      {emailSent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
          <div className="py-6 px-6 shadow-lg rounded-lg sm:px-8 w-full max-w-md relative">
            <div className="flex justify-end items-center mb-4">
              <button
                onClick={onCloseReset}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-[#06513D] mb-2">
                Reset Your Password
              </h2>
              <p className="text-sm text-gray-600">
                Enter the new password for your account.
              </p>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <InputField
                  label="New password"
                  name="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter your password"
                  type={passwordVisible ? "text" : "password"}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((prev) => !prev)}
                  className="absolute right-3 top-[30px] text-gray-500 hover:text-gray-700"
                >
                  {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {password && (
                <div className="mt-2 space-y-1">
                  {[
                    {
                      label: "At least 8 characters",
                      valid: passwordStrength.minLength,
                    },
                    {
                      label: "One uppercase letter",
                      valid: passwordStrength.hasUpperCase,
                    },
                    {
                      label: "One lowercase letter",
                      valid: passwordStrength.hasLowerCase,
                    },
                    { label: "One number", valid: passwordStrength.hasNumbers },
                    {
                      label: "One special character (@, #, $, etc.)",
                      valid: passwordStrength.hasSpecialChar,
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center text-[0.75rem]">
                      <div
                        className={`w-3 h-3 rounded-full mr-2 flex items-center justify-center ${
                          item.valid ? "bg-[#06513D]" : "bg-gray-300"
                        }`}
                      >
                        {item.valid && <Check className="w-2 h-2 text-white" />}
                      </div>
                      <span
                        className={
                          item.valid ? "text-[#06513D]" : "text-gray-500"
                        }
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <InputField
                label="Confirm new password"
                name="confirm_password"
                value={confirmPassword}
                onChange={handleConfirmPassword}
                type={"password"}
                error={Boolean(confirmPasswordError)}
                helperText={confirmPasswordError}
              />

              <button
                onClick={handleResetPassword}
                className="w-full py-2 px-4 rounded-md text-sm font-medium text-white bg-[#06513D] hover:bg-[#054a35] transition-colors"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
